import React, { useState, useEffect } from 'react';
import { frontendDb as db } from '../../lib/frontend-db';
import { Trash2, Upload, Plus } from 'lucide-react';

const YoungStarsPage = () => {
  const [settings, setSettings] = useState<any>({
    youngstars_enabled: false,
    youngstars_title: '',
    youngstars_logo_url: '',
    youngstars_content: '',
    youngstars_gallery: '[]'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [gallery, setGallery] = useState<string[]>([]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await db.getSettings() as any;
      if (data) {
        setSettings({
          youngstars_enabled: true, // Dauerhaft aktiv
          youngstars_title: data.youngstars_title || '',
          youngstars_logo_url: data.youngstars_logo_url || '',
          youngstars_content: data.youngstars_content || '',
          youngstars_gallery: data.youngstars_gallery || '[]'
        });
        
        try {
          const galleryData = JSON.parse(data.youngstars_gallery || '[]');
          setGallery(Array.isArray(galleryData) ? galleryData : []);
        } catch (e) {
          setGallery([]);
        }
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      if (error?.message?.includes('doesn\'t exist')) {
        setMessage('⚠️ Bitte führen Sie zuerst die Datenbank-Migration aus: mysql-migrations/20250112_stimmart_rebranding.sql');
      } else {
        setMessage('Fehler beim Laden der Einstellungen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      await db.updateSettings({
        youngstars_enabled: true, // Immer aktiv
        youngstars_title: settings.youngstars_title,
        youngstars_logo_url: settings.youngstars_logo_url,
        youngstars_content: settings.youngstars_content,
        youngstars_gallery: JSON.stringify(gallery)
      });
      
      setMessage('✅ Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('❌ Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setMessage('');

    try {
      console.log('📤 Starting logo upload:', file.name);
      const url = await db.storeImage(file);
      console.log('✅ Logo uploaded successfully:', url);
      setSettings({ ...settings, youngstars_logo_url: url });
      setMessage('✅ Logo erfolgreich hochgeladen!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('❌ Error uploading logo:', error);
      setMessage('❌ Fehler beim Hochladen des Logos: ' + (error as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setMessage('');

    try {
      console.log('📤 Starting gallery upload:', files.length, 'files');
      const uploadedUrls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        try {
          console.log(`📤 Uploading file ${i + 1}/${files.length}:`, files[i].name);
          const url = await db.storeImage(files[i]);
          console.log(`✅ File ${i + 1} uploaded successfully:`, url);
          uploadedUrls.push(url);
        } catch (error) {
          console.error(`❌ Error uploading file ${i + 1}:`, error);
          throw new Error(`Fehler beim Hochladen von ${files[i].name}: ${(error as Error).message}`);
        }
      }
      setGallery([...gallery, ...uploadedUrls]);
      setMessage(`✅ ${uploadedUrls.length} ${uploadedUrls.length === 1 ? 'Bild' : 'Bilder'} erfolgreich hochgeladen!`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('❌ Error uploading images:', error);
      setMessage('❌ Fehler beim Hochladen der Bilder: ' + (error as Error).message);
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const removeGalleryImage = (index: number) => {
    setGallery(gallery.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">StimmArt YoungStars</h2>
        <p className="text-gray-600">
          Verwalten Sie die YoungStars-Sektion mit Logo, Text und Bildergalerie.
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('✅') 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Info-Banner */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Die YoungStars-Sektion ist dauerhaft aktiviert und wird auf der Startseite angezeigt.
              </p>
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Titel *
          </label>
          <input
            type="text"
            value={settings.youngstars_title}
            onChange={(e) => setSettings({ ...settings, youngstars_title: e.target.value })}
            placeholder="z.B. StimmArt YoungStars"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo
          </label>
          {settings.youngstars_logo_url && (
            <div className="mb-4 flex justify-center">
              <img 
                src={settings.youngstars_logo_url} 
                alt="YoungStars Logo" 
                className="max-w-xs h-auto rounded-lg shadow-md"
              />
            </div>
          )}
          <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Upload size={20} className="mr-2" />
            {settings.youngstars_logo_url ? 'Logo ändern' : 'Logo hochladen'}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </label>
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Beschreibungstext
          </label>
          <textarea
            value={settings.youngstars_content}
            onChange={(e) => setSettings({ ...settings, youngstars_content: e.target.value })}
            placeholder="Beschreiben Sie die YoungStars-Gruppe..."
            rows={6}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            Verwenden Sie Zeilenumbrüche für Absätze
          </p>
        </div>

        {/* Gallery */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bildergalerie
          </label>
          
          {gallery.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
              {gallery.map((url, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={url} 
                    alt={`Bild ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => removeGalleryImage(index)}
                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <label className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
            uploading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-green-600 hover:bg-green-700 cursor-pointer'
          } text-white`}>
            <Plus size={20} className="mr-2" />
            {uploading ? 'Wird hochgeladen...' : 'Bilder hinzufügen'}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
          <p className="mt-2 text-sm text-gray-500">
            {gallery.length} {gallery.length === 1 ? 'Bild' : 'Bilder'} in der Galerie
            {gallery.length > 0 && ' · Sie können mehrere Bilder auf einmal hochladen'}
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Wird gespeichert...' : 'Änderungen speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default YoungStarsPage;


